import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import routes from './routes';
import { Auth } from '@/common';
import { SystemClaim } from '@/common/rbac';
import { isRouteInPlan } from '@/common/moduleAvailability';
import { AUTH_LOGOUT } from '@/store/actions';
import { KillWebsocket } from '@/websockets/notifications';
import { useAuthStore } from '@/stores/auth';
import { useSettingsStore } from '@/stores/settings';
import { pinia } from '@/stores/pinia';
import i18n from '@/i18n';

// ./routes exports its tree typed as `RoutePath[]`, a deliberately loose shape
// (every one of component/components/redirect is optional, so a single record
// can describe any of the route record variants). vue-router's
// `RouteRecordRaw` is a discriminated union that requires exactly one of
// those to be present per member, so it doesn't structurally match. The data
// itself is a valid set of route records (each one does have a
// component/components/redirect); cast through unknown rather than
// restructuring the shared route tree's typing.
const typedRoutes = routes as unknown as RouteRecordRaw[];

// vue-router's `RouteMeta` is an empty interface meant to be augmented by the
// app; without this, `to.meta.<field>` types as `unknown`, which breaks
// spreading `allowedClaims` into Auth.isAllowed below. Mirrors the `meta`
// shape declared on `RoutePath` in ./routes.ts.
declare module 'vue-router' {
	interface RouteMeta {
		title?: string;
		allowedClaims?: string[];
		hideForBillingType?: string[];
		hideForPaymentMethod?: string[];
		requiresNoAuth?: boolean;
		allowAuthenticated?: boolean;
		hideMarketingNavigation?: boolean;
		readOnly?: boolean;
		reuse?: boolean;
		reuseKey?: string;
		splitFallbackRouteName?: string;
		splitMainRouteName?: string;
	}
}

const router = createRouter({
	history: createWebHistory(),
	routes: typedRoutes,
	// linkActiveClass: 'active', // Vue Router 4 uses linkActiveClass prop on RouterLink or global config if needed, usually defaults are fine or configured differently
	scrollBehavior: (to, from, savedPosition) => {
		if (savedPosition) {
			return savedPosition;
		}
		if (to.hash) {
			return { el: to.hash };
		}
		if (to.path === from.path && to.query !== from.query) {
			return;
		}
		// Same reusable page (e.g. Inbox <-> Inbox Message, see routes.ts's
		// `reuseKey`) — the view never remounts for this, so jumping scroll to
		// the top would fight a sticky/side-panel layout for no reason.
		const toReuseKey = to.meta?.reuseKey;
		if (
			typeof toReuseKey === 'string' &&
			toReuseKey &&
			toReuseKey === from.meta?.reuseKey
		) {
			return;
		}
		return { left: 0, top: 0 };
	},
});

// A guard rejection lands on the no-access page, which says why: the module is
// not in the organisation's plan, the role does not cover the page, or the
// account's billing setup has no use for it. It used to be a toast over the
// dashboard, which a cold load could lose and which told the person nothing
// once it faded.
export type DenialReason = 'plan' | 'role' | 'account';

export const denyNavigation = (
	next: (location: { path: string; query: Record<string, string> }) => void,
	reason: DenialReason
) => {
	next({ path: '/no-access', query: { reason } });
};

const ignoredRoutes = [
	'/',
	'/home',
	'/login',
	'/dashboard/main',
	'/no-access',
	'/agents/history',
	'/release-notes',
	'/docs',
];
const SPLIT_VIEW_MIN_VIEWPORT_WIDTH = 960;

const allowsSharedPublicAccess = (to) =>
	to.matched.some(
		(record) => record.meta?.requiresNoAuth && record.meta?.allowAuthenticated
	);

const resolveUnauthenticatedRootRedirect = () => '/login';

const canRenderSplitViewOnViewport = () => {
	if (typeof window === 'undefined') {
		return true;
	}
	return window.innerWidth >= SPLIT_VIEW_MIN_VIEWPORT_WIDTH;
};

const resolveSplitViewDisabledRedirect = (to) => {
	const hasSplitViewRoute = to.matched.some((record) =>
		Boolean(record.components?.splitView)
	);
	if (!hasSplitViewRoute) {
		return null;
	}

	const settingsStore = useSettingsStore(pinia);
	const shouldUseSplitView =
		settingsStore.splitViewEnabled && canRenderSplitViewOnViewport();
	if (shouldUseSplitView) {
		return null;
	}

	const fallbackRouteName =
		typeof to.meta?.splitFallbackRouteName === 'string'
			? to.meta.splitFallbackRouteName
			: '';
	const mainRouteName =
		typeof to.meta?.splitMainRouteName === 'string'
			? to.meta.splitMainRouteName
			: '';
	const targetRouteName = fallbackRouteName || mainRouteName;
	const nextQuery = { ...to.query };
	delete nextQuery.splitTitle;

	if (
		targetRouteName &&
		targetRouteName !== to.name &&
		router.hasRoute(targetRouteName)
	) {
		const params = { ...to.params };
		const targetRoute = router
			.getRoutes()
			.find((r) => r.name === targetRouteName);
		const targetRequiresIdParam = targetRoute?.path?.includes(':id') ?? false;
		if (targetRequiresIdParam && !params.id && nextQuery.id) {
			params.id = nextQuery.id as string;
			delete nextQuery.id;
		}
		return {
			name: targetRouteName,
			params,
			query: nextQuery,
			replace: true,
		};
	}

	if (typeof to.path === 'string' && to.path.endsWith('/split')) {
		const fallbackPath = to.path.replace(/\/split$/, '');
		if (fallbackPath && fallbackPath !== to.path) {
			return {
				path: fallbackPath,
				query: nextQuery,
				replace: true,
			};
		}
	}

	return null;
};

// Check if user has only the 2FASetup claim
function hasOnly2FASetupClaim() {
	const claims = sessionStorage.getItem('user_claims');
	if (!claims) return false;
	const claimsArray = claims.split(',').map((c) => c.trim());
	return claimsArray.length === 1 && claimsArray[0] === SystemClaim.TwoFASetup;
}

router.beforeEach((to, from, next) => {
	const authStore = useAuthStore(pinia);
	const authAdapter = {
		getters: {
			isRootOrOwner: () => authStore.isRootOrOwner(),
			can: (role: string) => authStore.can(role),
		},
	};
	let savePrevPage = false;
	if (
		!ignoredRoutes.includes(to.path) &&
		!authStore.isAuthenticated() &&
		!localStorage.getItem('prevPage')
	) {
		savePrevPage = true;
		localStorage.setItem(
			'prevPage',
			window.location.pathname + window.location.search
		);
	}
	if (to.hash && to.hash.startsWith('#/')) {
		next({ path: '/', replace: true });
	}
	if (authStore.needs2FAVerification() && to.path !== '/two_factor') {
		// logout
		authStore[AUTH_LOGOUT]().then(() => {
			KillWebsocket();
			authStore.setRouterTo(null);
		});
	}

	// Handle 2FA setup flow for authenticated users
	if (authStore.isAuthenticated()) {
		if (hasOnly2FASetupClaim()) {
			if (to.path !== '/setup-mfa') {
				next('/setup-mfa');
				return;
			}
			next();
			return;
		}

		// Authenticated user without 2FASetup claim trying to access setup page
		if (to.path === '/setup-mfa') {
			next('/dashboard/main');
			return;
		}
	}
	let requiresNoAuth = to.matched.some((record) => record.meta.requiresNoAuth);
	let allowAuthenticated = allowsSharedPublicAccess(to);
	let allowedClaims =
		typeof to.meta.allowedClaims !== 'undefined' ? to.meta.allowedClaims : [];
	if (requiresNoAuth) {
		if (allowAuthenticated) {
			next();
			return;
		}
		if (
			(to.fullPath === '/' || to.fullPath === '/login') &&
			authStore.isAuthenticated()
		) {
			next('/dashboard/main');
		}
		if (to.path === '/login') {
			next();
		} else if (to.path === '/forgotpass' && from.path === '/login') {
			next();
		} else if (to.path === '/two_factor' && from.path === '/login') {
			next();
		} else if (
			to.path !== '/reset_password' &&
			to.path !== '/set_password' &&
			from.path !== '/'
		) {
			next(from);
		} else {
			next();
		}
	} else if (authStore.isAuthenticated()) {
		if (to.fullPath === '/' || to.fullPath === '/login') {
			next('/dashboard/main');
		} else {
			// The plan comes before the role, as it does in the sidebar: a page the
			// organisation has not bought is closed to everyone but Root, and no
			// role change would open it. Unknown availability lets the page through.
			if (
				authStore.isRoot?.() !== true &&
				!isRouteInPlan(allowedClaims, authStore.moduleAvailability)
			) {
				denyNavigation(next, 'plan');
				return;
			}
			if (Auth.isAllowed(authAdapter, ...allowedClaims)) {
				const hideForBillingType = to.meta.hideForBillingType;
				if (
					hideForBillingType &&
					hideForBillingType.length > 0 &&
					authStore.billingType &&
					hideForBillingType.includes(authStore.billingType)
				) {
					denyNavigation(next, 'account');
					return;
				}
				const hideForPaymentMethod = to.meta.hideForPaymentMethod;
				if (
					hideForPaymentMethod &&
					hideForPaymentMethod.length > 0 &&
					authStore.paymentMethod &&
					hideForPaymentMethod.includes(authStore.paymentMethod)
				) {
					denyNavigation(next, 'account');
					return;
				}
				const splitViewRedirect = resolveSplitViewDisabledRedirect(to);
				if (splitViewRedirect) {
					next(splitViewRedirect);
					return;
				}
				next();
			} else {
				denyNavigation(next, 'role');
			}
		}
	} else if (to.fullPath === '/') {
		next(resolveUnauthenticatedRootRedirect());
	} else {
		if (to && Object.keys(to.fullPath).length > 0) {
			authStore.setRouterTo(to);
		}
		next('/login');
	}
	if (!savePrevPage && from.path !== '/login' && from.path !== '/') {
		localStorage.removeItem('prevPage');
	}
});

export default router;
export {
	allowsSharedPublicAccess,
	ignoredRoutes,
	resolveUnauthenticatedRootRedirect,
};
